/**
 * Máscaras e validação de CNPJ / telefone (#231).
 *
 * Onboarding self-service: o backend valida CNPJ (`validate_cnpj`,
 * validation_service.py) removendo não-dígitos, mas o usuário não recebia
 * nenhum feedback ao digitar. Estas funções formatam progressivamente e
 * validam inline ANTES do submit, reduzindo fricção e dado quebrado no banco.
 *
 * O backend tolera valor formatado (re.sub(r'\D', '', cnpj)) — enviamos o
 * valor mascarado direto, sem normalizar pra dígitos crus.
 */

/** Remove tudo que não é dígito. */
export function onlyDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

/**
 * Máscara progressiva de CNPJ: XX.XXX.XXX/XXXX-XX.
 * Trunca em 14 dígitos. Formata conforme o usuário digita (parcial OK).
 */
export function maskCNPJ(value) {
  const d = onlyDigits(value).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/**
 * Máscara progressiva de telefone brasileiro.
 * Celular (11 díg): (XX) XXXXX-XXXX. Fixo (10 díg): (XX) XXXX-XXXX.
 * Trunca em 11 dígitos.
 */
export function maskTelefone(value) {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Formata um CNPJ para EXIBIÇÃO (#314). Recebe o valor como veio do backend
 * (só-dígitos ou já mascarado) e devolve XX.XXX.XXX/XXXX-XX. Se tiver os 14
 * dígitos, aplica a máscara; caso contrário devolve o que veio (sem quebrar).
 * Vazio/nulo → string vazia (o chamador decide não renderizar).
 */
export function formatCNPJ(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const d = onlyDigits(raw);
  if (d.length === 14) return maskCNPJ(d);
  return raw;
}

/**
 * Valida CNPJ via dígitos verificadores. Espelha o backend
 * `validate_cnpj` (validation_service.py:252) — mesmo algoritmo e pesos.
 */
export function isValidCNPJ(value) {
  const d = onlyDigits(value);
  if (d.length !== 14) return false;
  if (d === d[0].repeat(14)) return false;

  const calcDigit = (slice, weights) => {
    const total = slice
      .split('')
      .reduce((sum, ch, i) => sum + Number(ch) * weights[i], 0);
    const remainder = total % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const digit1 = calcDigit(d.slice(0, 12), weights1);
  const digit2 = calcDigit(d.slice(0, 12) + String(digit1), weights2);

  return d[12] === String(digit1) && d[13] === String(digit2);
}

/**
 * Valida telefone BR: aceita 10 (fixo) ou 11 (celular) dígitos.
 * Não verifica DDD nem 9º dígito — feedback leve, só completude.
 */
export function isValidTelefone(value) {
  const len = onlyDigits(value).length;
  return len === 10 || len === 11;
}

/** Máscara progressiva de CEP: XXXXX-XXX. Trunca em 8 dígitos. */
export function maskCEP(value) {
  const d = onlyDigits(value).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}
